import React, { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { Actionable, Icon, Image, Stack, Text } from "@bookingcom/bui-react";
import { ArrowRightIcon } from "@bookingcom/bui-assets-react/streamline";
import { t } from "@bookingcom/lingojs-core";
import { useI18n } from "@bookingcom/lingojs-react";
import { getFlightsInterlinking } from "../../../store/flightsInterlinking/selector.js";
import useGlobalContext from "../../../hooks/useGlobalContext.js";
import { LP_CITY_TO_CITY_URL, LP_CITY_URL, LP_AIRPORT_URL, LP_COUNTRY_URL, LP_REGION_URL, ACCOMMODATIONS_DOMAIN_URL, ACCOMMODATIONS_DOMAIN_URL_DQS } from "../../../constants.js";
import { IMAGE_STATIC_ASSETS_URL } from "@flights/constants";
import styles from "./FlightsInterlinkingItem.module.css";
const RenderItemContent = ({ image, text, secondaryText }) => {
    const i18n = useI18n();
    return (React.createElement(Stack, { direction: "row", alignItems: "center", gap: 4, wrap: "nowrap" },
        React.createElement(Image, { className: styles.image, src: `${IMAGE_STATIC_ASSETS_URL}${image}`, borderRadius: 200, fallback: "icon", width: "64", height: "64" }),
        React.createElement(Stack, { direction: "row", alignItems: "center", justifyContent: "start", gap: 2, className: styles.text, attributes: secondaryText
                ? {
                    "aria-label": i18n.trans(t("flights_cat_popular_route_title", {
                        variables: { origin_city: text, destination_city: secondaryText }
                    }))
                }
                : undefined },
            text ? (React.createElement(Text, { color: "neutral", variant: "strong_1", className: !secondaryText ? styles.text : "" }, text)) : null,
            secondaryText ? (React.createElement(React.Fragment, null,
                React.createElement(Icon, { size: "smallest", svg: ArrowRightIcon, color: "neutral", attributes: { style: { stroke: "currentColor", strokeWidth: 1 } } }),
                " ",
                React.createElement(Text, { color: "neutral", variant: "strong_1", className: !secondaryText ? styles.text : "" }, secondaryText))) : null)));
};
const FlightsInterlinkingItem = ({ flight }) => {
    const { env, locale } = useGlobalContext();
    const flightsInterlinking = useSelector(getFlightsInterlinking);
    const departureCountryCode = flightsInterlinking?.departureCountry?.countryCode || "";
    const getUrl = env === "prod" ? ACCOMMODATIONS_DOMAIN_URL : ACCOMMODATIONS_DOMAIN_URL_DQS;
    const { type, image } = flight;
    const { itemUrl, text, secondaryText } = useMemo(() => {
        let itemUrl = "";
        let text = "";
        let secondaryText;
        const localeUrl = locale === "en-us" ? "" : `${locale}.`;
        switch (type) {
            case "citiesToCities":
                itemUrl = `${getUrl}${LP_CITY_TO_CITY_URL}/${flight.countryCodeDeparture}-${flight.departurePageName}-to-${flight.countryCodeArrival}-${flight.arrivalPageName}.${localeUrl}html`;
                text = flight.departureTranslatedName || "";
                secondaryText = flight.arrivalTranslatedName;
                break;
            case "cities":
                itemUrl = `${getUrl}${LP_CITY_URL}/${flight.countryCode}/${flight.pageName}.${localeUrl}html`;
                text = flight.cityTranslatedName || "";
                break;
            case "countries":
                itemUrl = `${getUrl}${LP_COUNTRY_URL}/${departureCountryCode}/${flight.countryCode}.${localeUrl}html`;
                text = flight.countryTranslatedName || "";
                break;
            case "regions":
                itemUrl = `${getUrl}${LP_REGION_URL}/${flight.countryCode}/${flight.pageName}.${localeUrl}html`;
                text = flight.regionTranslatedName || "";
                break;
            case "airports":
                itemUrl = `${getUrl}${LP_AIRPORT_URL}/${flight.countryCode}/${flight.iata}.${localeUrl}html`;
                text = flight.airportTranslatedName && flight.iata ? `${flight.airportTranslatedName} (${flight.iata})` : "";
                break;
        }
        return { itemUrl, text, secondaryText };
    }, [type, flight, locale, departureCountryCode, getUrl]);
    const renderItem = useCallback(() => {
        if (!text)
            return null;
        return React.createElement(RenderItemContent, { image: image, text: text, secondaryText: secondaryText });
    }, [image, text, secondaryText]);
    return React.createElement(Actionable, { href: itemUrl }, renderItem());
};
export default FlightsInterlinkingItem;
